/*
 * CAPTURE-OWNED MODULE
 * SetupProposalPanel — guided setup v2, the review turn.
 *
 * THE ONE CLICK BETWEEN A PROPOSAL AND TENANT-GLOBAL SCHEMA. The 2026-09-02
 * ruling is explicit about why it exists: "These are tenant-global definitions
 * that surface in Library; reuse-first only works if a human can see 'reusing
 * delivery_date, creating driver_name' and say no. One extra click is the only
 * thing between a hurried reviewer and a junk definition that lives forever."
 *
 * SEPARATE FROM ClassificationEvidencePanel on purpose — that file already
 * carries turn one and its three-valued evidence rules — and separate so it is
 * testable with the createRoot + act harness the dialogs use (capture-ui has no
 * RTL). No network calls: the page owns fetching and applying.
 *
 * WHAT THE REVIEWER CAN CHANGE is bounded, and matches exactly what the apply
 * endpoint accepts: the detection keywords, a choice per ambiguity, and which
 * fields to drop. It never posts a proposal body — the stored server-authored
 * row is the authority and this sends a delta against it, because a builder who
 * could post arbitrary ops could mint arbitrary schema.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { summariseOps, buildEdits, canApply } from '../utils/setupProposal';
import './SetupProposalPanel.css';

const RESOLUTION_CREATE = '__create__';

/** A field_type is a vocabulary key; show it as a word, not a token. */
function typeLabel(fieldType) {
  return String(fieldType || 'text').replace(/_/g, ' ');
}

/**
 * @param {object|null} proposal  the stored SetupProposal
 * @param {boolean}     busy      an apply is in flight
 * @param {string|object|null} error  a message, or { message, code } — a
 *                                    `phrases_required` code renders inline on
 *                                    the keywords box rather than at the bottom
 * @param {Function}    onApply   called with the edits delta from buildEdits
 * @param {Function}    onCancel  dismiss without applying; writes nothing
 */
export default function SetupProposalPanel({ proposal, busy, error, onApply, onCancel }) {
  const { documentType, fields } = summariseOps(proposal);
  const ambiguities = Array.isArray(proposal?.ambiguities) ? proposal.ambiguities : [];
  const warnings = Array.isArray(proposal?.warnings) ? proposal.warnings : [];

  const [phrases, setPhrases] = useState('');
  const [resolutions, setResolutions] = useState({});
  const [skipped, setSkipped] = useState([]);

  // Re-seed when a DIFFERENT proposal arrives (a re-propose supersedes the old
  // row). Without this the reviewer's edits to the previous proposal would ride
  // silently onto the new one.
  useEffect(() => {
    setPhrases((documentType?.phrases || []).join(', '));
    setResolutions({});
    setSkipped([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal]);

  const toggleSkip = useCallback((field) => {
    setSkipped((prev) => {
      const indices = [field.opIndex, field.associationOpIndex].filter((n) => Number.isInteger(n));
      const isSkipped = prev.includes(field.opIndex);
      return isSkipped
        ? prev.filter((n) => !indices.includes(n))
        : [...prev, ...indices];
    });
  }, []);

  const chooseResolution = useCallback((wish, value) => {
    setResolutions((prev) => ({
      ...prev,
      [wish]: value === RESOLUTION_CREATE ? { create: true } : { field_key: value },
    }));
  }, []);

  // The same gate the server applies, so the button state matches what apply
  // will accept rather than promising a round trip that 400s.
  const applyable = canApply({ phrases, ambiguities, resolutions });

  const submit = useCallback(() => {
    if (!applyable || busy) return;
    onApply(buildEdits({ phrases, resolutions, skipped }));
  }, [applyable, busy, phrases, resolutions, skipped, onApply]);

  if (!proposal || !documentType) return null;

  const errorMessage = typeof error === 'string' ? error : error?.message || null;
  const errorCode = typeof error === 'object' && error ? error.code : null;
  const phraseError = errorCode === 'phrases_required' ? errorMessage : null;
  const generalError = errorCode === 'phrases_required' ? null : errorMessage;

  const hasSuggestedPhrases = (documentType.phrases || []).length > 0;

  return (
    <div className="rd-reextract-panel sp-panel">
      <div className="rd-reextract-title">Set up “{documentType.label}”</div>
      <div className="sp-lede">
        This creates the document type and its index fields for the whole
        workspace. Nothing is written until you press Create.
      </div>

      {/* ── Detection keywords ─────────────────────────────────────────── */}
      <div className="sp-section">
        <label className="sp-field-label" htmlFor="sp-phrases">
          Detection keywords (comma-separated) — required
        </label>
        <input
          id="sp-phrases"
          className="sp-input"
          type="text"
          value={phrases}
          maxLength={1000}
          placeholder="e.g. delivery note, received by, consignment"
          onChange={(e) => setPhrases(e.target.value)}
          disabled={busy}
        />
        {!hasSuggestedPhrases && (
          <div className="sp-hint">
            {documentType.rationale && (
              <span className="sp-rationale">{documentType.rationale}</span>
            )}{' '}
            Type words that appear on this document and will appear on the next
            one of its kind. They are how documents of this type get recognised
            — without any, this type would never match anything.
          </div>
        )}
        {phraseError && <div className="sp-error sp-error--field">{phraseError}</div>}
      </div>

      {/* ── Fields ─────────────────────────────────────────────────────── */}
      {fields.length > 0 && (
        <div className="sp-section">
          <div className="sp-section-title">Index fields</div>
          <ul className="sp-fields">
            {fields.map((f) => {
              const isSkipped = skipped.includes(f.opIndex);
              return (
                <li key={f.key} className={`sp-field${isSkipped ? ' sp-field--skipped' : ''}`}>
                  <div className="sp-field-main">
                    <span className="sp-field-name">{f.label}</span>
                    <span className="sp-field-type">{typeLabel(f.field_type)}</span>
                    <span className={`sp-badge sp-badge--${f.reused ? 'reuse' : 'new'}`}>
                      {f.reused ? 'reusing an existing field' : 'new field'}
                    </span>
                    {f.tenant_fork_inactive && (
                      <span className="sp-badge sp-badge--warn">
                        currently disabled — will be re-enabled
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="sp-remove"
                    onClick={() => toggleSkip(f)}
                    disabled={busy}
                    title={isSkipped ? 'Put this field back' : 'Do not create this field'}
                  >
                    {isSkipped ? 'Undo' : 'Remove'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Ambiguities ────────────────────────────────────────────────── */}
      {ambiguities.length > 0 && (
        <div className="sp-section">
          <div className="sp-section-title">Needs your decision</div>
          {ambiguities.map((a, i) => (
            <fieldset className="sp-ambiguity" key={a.wish}>
              <legend className="sp-ambiguity-wish">“{a.wish}” — which field?</legend>
              {(a.candidates || []).map((c) => (
                <label className="sp-choice" key={c.field_key}>
                  <input
                    type="radio"
                    name={`sp-ambiguity-${i}`}
                    value={c.field_key}
                    checked={resolutions[a.wish]?.field_key === c.field_key}
                    onChange={() => chooseResolution(a.wish, c.field_key)}
                    disabled={busy}
                  />
                  <span className="sp-choice-label">
                    Use <strong>{c.label || c.field_key}</strong>
                    {c.why && <span className="sp-choice-why"> — {c.why}</span>}
                  </span>
                </label>
              ))}
              <label className="sp-choice" key="__create__">
                <input
                  type="radio"
                  name={`sp-ambiguity-${i}`}
                  value={RESOLUTION_CREATE}
                  checked={resolutions[a.wish]?.create === true}
                  onChange={() => chooseResolution(a.wish, RESOLUTION_CREATE)}
                  disabled={busy}
                />
                <span className="sp-choice-label">Create a new field instead</span>
              </label>
            </fieldset>
          ))}
        </div>
      )}

      {/* ── Warnings ───────────────────────────────────────────────────── */}
      {warnings.length > 0 && (
        <div className="rd-instructions sp-warnings">
          <div className="rd-instructions-label">Before you continue</div>
          <ul className="sp-warning-list">
            {warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      {generalError && <div className="sp-error">{generalError}</div>}

      <div className="sp-actions">
        <button
          type="button"
          className="sp-btn sp-btn--primary"
          onClick={submit}
          disabled={busy || !applyable}
        >
          {busy ? 'Creating…' : 'Create it'}
        </button>
        <button type="button" className="sp-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
